import { type CalendarDate, type DateRange, daysBetween } from '@room-mates/shared';

export type Language = 'en' | 'fr';

const BCP47_LOCALE: Record<Language, string> = { en: 'en-US', fr: 'fr-FR' };

/** Set once by `LanguageProvider` on load and on every toggle; read by every formatter below. */
let currentLocale: string = BCP47_LOCALE.en;

export function setFormatLocale(language: Language): void {
  currentLocale = BCP47_LOCALE[language];
}

/** For call sites that build their own `Intl` formatter, such as a calendar's month grid. */
export function getFormatLocale(): string {
  return currentLocale;
}

/** Renders a calendar date in the current language's notation without shifting the day. */
export function formatDate(date: string): string {
  return new Intl.DateTimeFormat(currentLocale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

export function formatDateTime(instant: string): string {
  return new Intl.DateTimeFormat(currentLocale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(instant));
}

/** Today, as a calendar date — UTC, matching how the domain treats every other date. */
export function todayIso(): CalendarDate {
  return new Date().toISOString().slice(0, 10);
}

export function formatRange(range: DateRange): string {
  return `${formatDate(range.start)} → ${formatDate(range.end)}`;
}

export function nightsInRange(range: DateRange): number {
  return daysBetween(range.start, range.end);
}

/** How a room is named anywhere outside its own house page. */
export function roomLabel(room: { readonly houseName: string; readonly name: string }): string {
  return `${room.houseName} — ${room.name}`;
}

/** `+120` / `−40`, or a dash when a reason carries no weight. */
export function formatWeight(weight: number): string {
  if (Math.abs(weight) < 0.005) {
    return '—';
  }
  const rounded = Math.round(weight * 10) / 10;
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)}`;
}
